import { setTag, setUser } from "@sentry/react"
import { useEffect } from "react"

import type { Org } from "@/api/orgs"
import type { User } from "@/api/users"
import { getInstanceIdPrefix } from "@/util/instance"
import { loadIntercom } from "@/util/intercom"
import { logEvent } from "@/util/logging"
import type { JsonObject } from "@/util/types"
import { ROOT_HOST, viewManager } from "@/util/viewManager"

// Third-party analytics SDKs attach themselves to `window` at runtime. We
// access them through a narrow structural type rather than `any` so
// property accesses remain type-checked.
interface AnalyticsClient {
  identify?: (id: string, traits?: Record<string, unknown>) => void
  page?: (params?: unknown) => void
  track?: (params?: unknown) => void
}

interface AnalyticsWindow {
  analytics?: AnalyticsClient
  fullstory?: AnalyticsClient
  logRocket?: AnalyticsClient
  posthog?: AnalyticsClient
}

function getAnalyticsWindow(): AnalyticsWindow {
  return window as unknown as AnalyticsWindow
}

export function identify(org: Org, user: User, metadata?: Record<string, unknown>) {
  if (process.env.NODE_ENV === "development") {
    logEvent("info", "analytics: identify", { extra: { org, user, metadata } })
    return
  }

  const { name, email, orgId } = user
  const { salesforceId } = org
  const parentOrg = org.parentOrg ?? undefined
  const baseOrg = parentOrg ?? org

  // Fires the intercom boot + identify logic for logged in users
  loadIntercom(user, org)

  const { analytics, fullstory, logRocket, posthog } = getAnalyticsWindow()

  fullstory?.identify?.(String(user.id), {
    displayName: name,
    email,
    orgId,
    orgName: org.name,
    parentOrgId: parentOrg?.id,
    parentOrgName: parentOrg?.name,
    baseOrgId: baseOrg.id,
    baseOrgName: baseOrg.name,
    salesforceId,
    ...metadata,
  })

  logRocket?.identify?.(String(user.id), {
    name,
    email,
    org_id: orgId,
    org_name: org.name,
    parent_org_id: parentOrg?.id,
    parent_org_name: parentOrg?.name,
    base_org_id: baseOrg.id,
    base_org_name: baseOrg.name,
    salesforce_id: salesforceId,
    ...metadata,
  })

  posthog?.identify?.(String(user.id), {
    name,
    email,
    orgId,
    orgName: org.name,
    parentOrgId: parentOrg?.id,
    parentOrgName: parentOrg?.name,
    baseOrgId: baseOrg.id,
    baseOrgName: baseOrg.name,
    salesforceId,
    ...metadata,
  })

  if (setUser) {
    setUser({ email })

    setTag("org", baseOrg.subdomain)

    const embedId = viewManager.get("org")?.embed?.id ?? org.embed?.id
    setTag("embedId", embedId)
  }

  analytics?.identify?.(`${getInstanceIdPrefix()}${user.id}`, {
    name,
    email,
    org_id: orgId,
    org_name: org.name,
    parent_org_id: parentOrg?.id,
    parent_org_name: parentOrg?.name,
    base_url: ROOT_HOST,
    base_org_id: baseOrg.id,
    base_org_name: baseOrg.name,
    salesforce_id: salesforceId,
    ...metadata,
  })
}

export function page(params?: string | JsonObject) {
  logEvent("info", "analytics: page event", { extra: { params } })
  const { analytics } = getAnalyticsWindow()
  analytics?.page?.(params)
}

export function track(params?: string | JsonObject) {
  logEvent("info", "analytics: track event", { extra: { params } })
  const { analytics } = getAnalyticsWindow()
  analytics?.track?.(params)
}

export function usePageTracking(pageName: string) {
  useEffect(() => page(pageName), [pageName])
}
